import { Injectable } from '@angular/core';
import {
  CanActivate,
  CanActivateChild,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  UrlTree,
  Router,
} from '@angular/router';
import { Observable } from 'rxjs';
import { AuthService } from './auth.service';
import { BlacklistUsersService } from './email-blacklist.service';
import { ApprovedUsersService } from './approved-users.service';
import { EMAIL_BLACKLIST_CONFIG } from '../config/email-blacklist.config';

@Injectable({
  providedIn: 'root',
})
export class EmailBlacklistGuard implements CanActivate, CanActivateChild {
  constructor(
    private authService: AuthService,
    private blacklistUsersService: BlacklistUsersService,
    private approvedUsersService: ApprovedUsersService,
    private router: Router
  ) {}

  /**
   * Check if the current user's email is blacklisted
   * Whitelist takes precedence - whitelisted users can override blacklist restrictions
   * @param path - The route path being accessed (optional, for path-specific checks)
   * @returns boolean - False if user is blacklisted, true otherwise
   */
  private checkEmailBlacklist(path?: string): boolean {
    const currentUser = this.authService.currentUserValue;

    console.log('EmailBlacklistGuard: Checking blacklist for path', path);
    console.log('EmailBlacklistGuard: Current user', currentUser);
    
    // First check if user is logged in
    if (!currentUser || !currentUser.email) {
      console.warn('EmailBlacklistGuard: No authenticated user or user email found');
      this.router.navigate([EMAIL_BLACKLIST_CONFIG.loginRedirectPath]);
      return false;
    }

    // Check if user's email is expired (reuse existing logic from AuthGuard)
    if (currentUser.expiredAt && currentUser.expiredAt * 1000 < new Date().getTime() + 1) {
      console.warn('EmailBlacklistGuard: User session expired');
      this.router.navigate([EMAIL_BLACKLIST_CONFIG.loginRedirectPath]);
      return false;
    }

    // WHITELIST CHECK FIRST: If user is whitelisted, allow access regardless of blacklist
    const isWhitelisted = this.approvedUsersService.isEmailApproved(currentUser.email.toLowerCase());
    if (isWhitelisted) {
      console.log(`EmailBlacklistGuard: User email '${currentUser.email}' is whitelisted - allowing access (whitelist overrides blacklist)`);
      return true;
    }

    // Check if user's email is blacklisted (globally or for specific path)
    const isBlacklisted = this.blacklistUsersService.isEmailBlacklisted(currentUser.email.toLowerCase(), path);
    
    if (isBlacklisted) {
      console.warn(`EmailBlacklistGuard: User email '${currentUser.email}' is blacklisted${path ? ` for path '${path}'` : ' globally'}`);
      this.router.navigate([EMAIL_BLACKLIST_CONFIG.unauthorizedRedirectPath]);
      return false;
    }

    return true;
  }

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    // Pass the activated route path for path-specific blacklist checking
    return this.checkEmailBlacklist(state.url);
  }

  canActivateChild(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    // Pass the activated route path for path-specific blacklist checking
    return this.checkEmailBlacklist(state.url);
  }
}
